import {Avatar, Input, Switch} from 'antd'
import {getId} from 'apis/model/base'
import {createSelectValue, createValue} from 'components/form/utils'
import {Formik} from 'formik'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import {useOrgTypeLabel} from 'modules/local/useOrgTypeLabels'
import withTranslate from 'modules/local/withTranslate'
import React, {useMemo} from 'react'
import {compose, mapProps, nest, pure} from 'recompose'
import {createControlledFormField} from 'views/AppLocals/factory/createFormField'
import {ErrorSummaries} from 'views/AppLocals/factory/createEditableForm'
import {NullAsDefault, shallowDiff} from 'views/Shared'
import CourseClassInformation from 'views/AppLocals/Course/CourseClass/CourseClassInformation'
import {ObjectSelectField} from 'components/form/fields/ObjectSelectField'
import {CourseClassStudentPicker} from 'views/Workspace/Course/containers/wrappers/CourseClassStudentPicker'
import TooltipApp from 'components/TooltipApp'
import {SelectWidget} from 'views/AppLocals/custom/ReactSelectComponents'
import CourseCoverField from 'views/AppLocals/Course/CourseSet/components/CourseCoverField'
import LookupField from 'components/LookupField'
import Translate from 'modules/local/Translate'
import {AnnouncementTypes} from 'views/AppLocals/Organization/consts/AnnouncementTypes'
import dayjs from 'dayjs'
import {createDateComponent} from 'components/form/fields/DateField'
import {
    dateAfter,
    lengthBetween,
    noDoubleSpaces,
    noLeadingTrailingSpaces,
    noNumber,
    noSpecialChars,
    required,
    runRules,
    touchFields,
} from 'components/form/ValidateForm'
import {getDateString} from 'helpers/datetimeHelpers'
import {BsChevronRight} from "react-icons/bs";
import {Field, FormActions, Section} from "components/form/ModernForm";
import {
    IoCalendarOutline,
    IoChatbubbleOutline,
    IoLinkOutline,
    IoMegaphoneOutline,
    IoPeopleOutline
} from "react-icons/io5";

// Tách thành component đặt tên PascalCase (không phải arrow function ẩn danh gán vào key `component`)
// vì eslint rule "react-hooks/rules-of-hooks" chỉ nhận diện hook hợp lệ trong component/hook đặt tên đúng chuẩn.
const ALL_GROUPS_VALUE = '__organization__'
const GroupsInOrganizationField = ({name, value, form, onChange}) => {
    const t = useTranslate()
    // owner_type='organization' luôn nghĩa là ownerItem = organization (xem useAnnouncementForm.js).
    const organization = _.get(form, 'values.ownerItem', {})
    // Tên gọi "group/ministry" phụ thuộc org_type — vd Giáo xứ: "Hội đoàn", Giáo phận: "Ủy ban", Dòng tu: "Cộng đoàn"...
    const deptLabel = useOrgTypeLabel(organization?.org_type, 'group', organization?.label_privacy) || t('groups / ministries')
    const groupOptions = form?.values?.groups?.map(e => ({
        value: e.id,
        label: `${e?.title} (${e?.member_count} ${t('members')})`,
    })) || []
    const options = [
        {value: ALL_GROUPS_VALUE, label: t('notify entire organization')},
        ...groupOptions,
    ]
    const selectedGroup = groupOptions.find((o) => o.value === value)

    return (
        <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-color-200 tracking-wider pr-3">
                {deptLabel}
            </span>
            <SelectWidget
                name={name}
                allowClear
                className="w-full"
                value={value || ALL_GROUPS_VALUE}
                onChange={(v) => onChange(createSelectValue('group_id', v))}
                placeholder={t('notify all groups')}
                options={options}
            />
            <span className="mt-1 px-2 py-1 italic bg-slate-50 rounded-lg">
                <span className="font-semibold text-slate-500 flex-shrink-0">
                    {`* ${t('note')} : `}
                </span>
                <span className="text-slate-400">
                    {selectedGroup ? t('announcement scoped to group', {group: selectedGroup.label}) : t('announcement scope description')}
                </span>
            </span>
        </div>
    )
}

const formSchema = {
    title: {
        title: 'title',
        children: [
            {
                name: 'title',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, translate, onChange}) => ({
                        showCount: true,
                        maxLength: 180,
                        name,
                        value,
                        onChange,
                        placeholder: translate('title'),
                    })),
                    pure
                )(Input),
            },
        ],
    },

    content: {
        title: 'content',
        children: [
            {
                name: 'content',
                component: createControlledFormField({
                    showCount: true,
                    maxLength: 500,
                    rows: 5,
                    placeholder: 'content',
                    Component: Input.TextArea,
                }),
            },
        ],
    },

    photo: {
        title: 'image',
        children: [
            {
                name: 'photo',
                component: ({name, value, form, onChange}) => (
                    <Translate>
                        {(t) => (
                            <CourseCoverField
                                cropPurpose="announcement"
                                selectedLabel={t('announcement image selected')}
                                note={t('announcement image note')}
                                values={_.get(form, 'values')}
                                {...{name, value, onChange}}
                            />
                        )}
                    </Translate>
                ),
            },
        ],
    },

    shared_url: {
        title: 'shared url',
        description: 'optional link for more information',
        children: {
            name: 'shared_url',
            component: compose(
                withTranslate,
                mapProps(({value, onChange, meta, translate}) => {
                    const error = meta?.error;
                    const touched = meta?.touched;
                    return {
                        value,
                        onChange,
                        name: 'shared_url',
                        title: touched && error ? error : '',
                        open: touched && !!error,
                        status: touched && error ? 'error' : '',
                        addonBefore: 'https://...',
                        placeholder: translate('please input url'),
                        type: 'text',
                    };
                }),
                pure
            )(nest(TooltipApp, Input)),
        },
    },

    groups_in_organization: {
        // Không dùng title/description tĩnh — tên "group/ministry" phụ thuộc org_type,
        // GroupsInOrganizationField tự resolve và tự vẽ label (xem định nghĩa phía trên).
        children: [
            {
                name: 'group_id',
                component: GroupsInOrganizationField,
            },
        ],
    },

    subject: {
        title: 'subject',
        children: [
            {
                name: 'subject',
                component: mapProps(({name, value, form, onChange}) => {
                    const {course_class} = form?.values ?? {}
                    const {class_subjects = []} = course_class ?? {};
                    const subjects = class_subjects.map(e => ({
                        id: e.subject.id,
                        value: e.subject.id,
                        label: e.subject.title,
                        ...e.subject
                    }));

                    return {
                        name,
                        onChange,
                        // form.setFieldValue (Formik) THAY VÌ gọi lại `onChange` bắt qua mapProps -
                        // `onChange` ở đây là field.onChange thô của Formik (do FieldsFactory spread
                        // {...field} vào props), react-select (SelectWidget bên trong ObjectSelectField)
                        // giữ closure onChange NỘI BỘ nên có thể chạy callback với 1 bản closure CŨ (từ
                        // lần render trước đó của chính field này) - lúc đó `onChange` bị stale/undefined
                        // gây lỗi "onChange is not a function" khi chọn Môn học (bạn báo). setFieldValue
                        // là method ổn định của Formik, không đổi theo từng lần render field, tránh hẳn
                        // stale closure này - cùng cách touchFields()/setFieldTouched() đang dùng ở
                        // CourseSetForm.js/CourseClassForm.js cho field custom khác.
                        onOptionChange: (option) => {
                            // if-check thay vì `form?.setFieldValue?.(...)` (optional call đứng riêng
                            // 1 dòng bị eslint no-unused-expressions báo lỗi build với config CRA hiện
                            // tại - parser không nhận diện ExpressionStatement bọc OptionalCallExpression
                            // là 1 lệnh gọi hàm hợp lệ).
                            if (form?.setFieldValue) {
                                form.setFieldValue(name, option ?? null)
                            }
                        },
                        // Đọc THẲNG `value` (field.value thật của Formik = values.subject, do
                        // FieldsFactory spread {...field}) thay vì tra theo `subject_id` - field này
                        // tên là "subject" (khớp name khai báo phía trên), giá trị lưu xuống form
                        // (qua onOptionChange) là NGUYÊN object subject vừa chọn (option), không phải
                        // 1 id rời rạc ở field "subject_id" (field đó không tồn tại trong form này,
                        // tra theo nó luôn ra undefined - Select không bao giờ hiện lại đúng lựa chọn
                        // đã chọn, dù chọn thành công).
                        value: value?.id,
                        showSearch: true,
                        options: subjects,
                        style: {
                            padding: 0,
                            border: 0,
                        }
                    }
                })(ObjectSelectField),
            },
        ],
    },

    is_for_all: {
        title: 'for all students',
        description: 'notify all students in the class',
        children: [
            {
                name: 'is_for_all',
                component: ({name, value, onChange}) => (
                    <Translate>
                        {(t) => (
                            <Switch
                                checked={value}
                                checkedChildren={<span className="font-medium italic">{t('yes')}</span>}
                                onChange={(checked) => {
                                    onChange(createValue(name, Number(checked)))
                                }}
                                className="w-fit"
                            />
                        )}
                    </Translate>
                ),
            },
        ],
    },

    students: {
        title: 'students',
        description: 'select specific students to receive this announcement',
        children: [
            {
                name: 'student_ids',
                component: mapProps(({name, value, form, onChange}) => {
                    const {course_class_id} = form?.values ?? {}
                    return {
                        name,
                        defaultValue: value,
                        mode: "multiple",
                        onChange: (value) => {
                            onChange(createValue(name, value))
                        },
                        course_class_id
                    }
                })(CourseClassStudentPicker),
            },
        ],
    },

    category: {
        title: 'announcement type',
        children: [
            {
                name: 'type',
                component: ({name, value, form}) => {
                    const values = _.get(form, 'values', {})
                    // course VÀ course_class: dùng CHUNG bucket lookup 'course_class' - 2 owner_type
                    // sibling trong cùng module Đào tạo & Huấn luyện (cả khóa/từng lớp), cùng nhu cầu loại
                    // thông báo (lịch thi, hỗ trợ học phí/học bổng, nghỉ học...) khác hẳn đời sống giáo xứ
                    // (Rửa tội, Cưới hỏi, Cáo phó...) mà org_type suy ra - không tách thêm bucket 'course'
                    // rỗng riêng, tái dùng vocabulary course_class đã xây qua LookUp động (createIfNotExists).
                    const isCourseScoped = ['course_class', 'course'].includes(_.get(values, 'owner_type'))
                    // owner_type='organization'|'group': object đã chọn LUÔN nằm ở `ownerItem` (xem
                    // useAnnouncementForm.js) - organization có org_type, group (standalone) có
                    // group_type, 2 field không đụng nhau nên đọc chung 1 lượt không cần if/else riêng.
                    const ownerItem = _.get(values, 'ownerItem', {})
                    const typeBucketKey = isCourseScoped
                        ? 'course_class'
                        : (ownerItem?.org_type || ownerItem?.group_type || 'default')
                    return (
                        <Translate>
                            {(t) => (
                                <LookupField
                                    createIfNotExists={true}
                                    defaultValue={values?.type_label}
                                    valueType="value"
                                    apiPath={`/lookup/organization_announcement_type/gets?group_type=organization_announcement_${typeBucketKey}`}
                                    name={name}
                                    placeholder={t('choose or create')}
                                    handleChange={(e) => {
                                        // type_code PHẢI luôn là 1 mã CỐ ĐỊNH trong AnnouncementTypes -
                                        // quy định icon/màu hiển thị, dùng chung ở nhiều nơi
                                        // (WorkspaceOrganizationAnnouncements, CourseClassInforTab,
                                        // OrganizationInfo... đều fallback `type_code || type_label` khi
                                        // tìm icon). Khi người dùng tự gõ type_label MỚI (nhánh
                                        // CreateKeywordRow trong LookupField, không chọn từ option có sẵn)
                                        // thì e.target.code luôn rỗng - trước đây lưu thẳng type_code=NULL
                                        // xuống DB, hỏng icon/màu về sau (thấy rõ ở data thật: nhiều dòng
                                        // course_class type_code NULL). Fallback về 'OTHER' (mã catch-all
                                        // có sẵn, không bịa mã mới) để type_code luôn hợp lệ - type_label
                                        // vẫn giữ nguyên text người dùng gõ để hiển thị.
                                        form.setValues({
                                            ...form.values,
                                            type_label: e?.target?.value,
                                            type_code: e?.target?.code || 'OTHER',
                                        });
                                    }}
                                    OptionWrapper={({children, ...props}) =>
                                        <div className="lookup-item" {...props}>
                                            {children}
                                        </div>
                                    }
                                    renderOption={(o, i) => {
                                        return (
                                            <div key={i} className="flex items-center gap-3 flex-1">
                                                <Avatar
                                                    size={50}
                                                    icon={_.find(AnnouncementTypes, {value: o?.code})?.icon}
                                                    className="flex flex-center background rounded-lg"
                                                />
                                                <div className="flex flex-col flex-1">
                                                    <div
                                                        className="font-bold text-base text-color-000 tracking-wide leading-snug">
                                                        {t(o?.label)}
                                                    </div>
                                                    <div className="text-xs text-color-300 italic leading-snug">
                                                        {o?.label}
                                                    </div>
                                                </div>
                                                <BsChevronRight size={16} className="text-color-500"/>
                                            </div>
                                        )
                                    }}
                                />
                            )}
                        </Translate>
                    )
                },
            },
        ],
    },

    started_finished_date: {
        inline: true,
        description: 'announcement display period',
        className: 'grid grid-cols-1 md:grid-cols-2 gap-3',
        children: [
            {
                name: 'time_open',
                label: 'started at',
                component: createDateComponent(),
            },
            {
                name: 'time_close',
                label: 'finished at',
                component: createDateComponent(),
            },
        ],
    },
}

// Tái dùng thẳng các component field đã có sẵn trong formSchema (Input tựa đề, TextArea nội dung,
// PhotoField, LookupField loại thông báo, DateField thời gian...) thay vì viết lại - chỉ đổi khung
// ngoài (Section/Field của ModernForm) cho Create/UpdateForm, cùng pattern đã dùng ở
// ParishForm.js/ProfileForm.js. Xem docs/ui/form-layout.md.
const TitleInput = formSchema.title.children[0].component
const ContentInput = formSchema.content.children[0].component
const PhotoInput = formSchema.photo.children[0].component
// shared_url.children là object đơn (không phải mảng như các field khác) - giữ nguyên định nghĩa
// gốc trong formSchema, chỉ trích xuất đúng shape của nó.
const SharedUrlInput = formSchema.shared_url.children.component
const SubjectInput = formSchema.subject.children[0].component
const IsForAllInput = formSchema.is_for_all.children[0].component
const StudentsInput = formSchema.students.children[0].component
const CategoryInput = formSchema.category.children[0].component
const TimeOpenInput = formSchema.started_finished_date.children[0].component
const TimeCloseInput = formSchema.started_finished_date.children[1].component

const validateTitle = (title, t) => {
    const rules = [
        required(t),
        noLeadingTrailingSpaces(t),
        noDoubleSpaces(t),
    ].filter(Boolean)
    const message = runRules(title, rules)
    if (message) return {title: message}
}

const validateType = (typeName, t) => {
    const rules = [
        lengthBetween(1, 120, t),
        noNumber(t),
        noSpecialChars(t),
        noLeadingTrailingSpaces(t),
        noDoubleSpaces(t),
    ].filter(Boolean)
    const message = runRules(typeName, rules)
    if (message) return {type: message}
}

const validateShareUrl = (shareUrl, t) => {
    if (!shareUrl) return {}
    try {
        const u = new URL(
            /^https?:\/\//i.test(shareUrl)
                ? shareUrl
                : 'https://' + shareUrl
        )
        if (!u.hostname.includes('.')) {
            return {shared_url: t('invalid url')}
        }
        return {}
    } catch {
        return {shared_url: t('invalid url')}
    }
}

const validateTimeOpen = (timeOpen, typeForm, t) => {
    const rules = [required(t)].filter(Boolean)
    const message = runRules(timeOpen, rules)
    if (message) return {time_open: message}
    if (typeForm === 'Create') {
        const selected = dayjs(timeOpen).startOf('day')
        const today = dayjs().startOf('day')
        if (selected.isBefore(today)) {
            return {time_open: t('start date must be today or later')}
        }
    }
}

const validateTimeClose = (timeOpen, timeClose, t) => {
    // Không bắt buộc - để trống nghĩa là thông báo chỉ có hiệu lực trong ngày time_open (hết hạn
    // lúc 23:59:59 cùng ngày), xem Announcement::effectiveEndSql() ở backend.
    if (!timeClose) return

    // inclusive=true - thông báo có thể bắt đầu/kết thúc CÙNG 1 ngày (chỉ hiệu lực trong ngày đó).
    const message = dateAfter(timeOpen, t, {inclusive: true})(timeClose)
    if (message) return {time_close: message}
}

const validate = (t, typeForm) => (values) => {
    const {
        title,
        shared_url,
        type_label,
        time_open,
        time_close
    } = values ?? {}
    const errorTitle = validateTitle(title, t)
    const errorType = validateType(type_label, t)
    const errorShareUrl = validateShareUrl(shared_url, t)
    const errorTimeOpen = validateTimeOpen(time_open, typeForm, t)
    const errorTimeClose = validateTimeClose(time_open, time_close, t)
    return {
        ...errorTitle,
        ...errorType,
        ...errorShareUrl,
        ...errorTimeOpen,
        ...errorTimeClose,
    }
}

const normalizeShareUrl = (shared_url) => shared_url
    ? (/^https?:\/\//i.test(shared_url) ? shared_url : 'https://' + shared_url)
    : shared_url

export const AnnouncementCreateForm = ({
                                           owner_type,
                                           ownerItem,
                                           groups,
                                           initialValues,
                                           onCancel = NullAsDefault,
                                           onSubmit = NullAsDefault,
                                           isSubmitting = false,
                                       }) => {
    const t = useTranslate()
    const isCourseClass = owner_type === 'course_class'
    const isOrganization = owner_type === 'organization'
    // course_class có UI nhận thông báo RIÊNG (subject/is_for_all/students - xem Section "recipients"
    // dưới) nên vẫn giữ 1 key Formik values tên `course_class` (không dùng chung `ownerItem` như 3
    // owner_type còn lại) - CourseClassInformation/SubjectInput/CourseClassStudentPicker đọc key này.
    const course_class = isCourseClass ? ownerItem : undefined

    const _initialValues = useMemo(
        () => ({
            title: undefined,
            content: undefined,
            photo: undefined,
            type_label: t('ANNOUNCEMENT'),
            type_code: 'ANNOUNCEMENT',
            time_open: dayjs().set("hour", 0).set("minute", 0).set("second", 0),
            time_close: dayjs().add(7, 'day').set("hour", 0).set("minute", 0).set("second", 0),
            owner_type,
            ...(isCourseClass
                ? {
                    is_for_all: 1,
                    course_class,
                    course_class_id: course_class?.id,
                    student_ids: [],
                }
                // group/course/organization: KHÔNG có field nhận thông báo riêng nào khác -
                // chỉ cần đúng 1 object owner đã chọn (+ `groups` phụ lục nếu là organization).
                : {
                    ownerItem,
                    ...(isOrganization ? {groups} : {}),
                }),
            ...(initialValues ?? {}),
        }),
        [owner_type, ownerItem, groups, course_class, initialValues]
    )

    return (
        <Formik
            validate={validate(t, 'Create')}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={_initialValues}
            onSubmit={(values) => {
                if (isCourseClass) {
                    const {
                        subject, course_class, shared_url, time_open, time_close, ...rest
                    } = values ?? {}
                    onSubmit(
                        _.omitBy(
                            {
                                shared_url: normalizeShareUrl(shared_url),
                                time_open: getDateString(time_open),
                                time_close: getDateString(time_close),
                                ...rest,
                                subject_id: !!values?.subject_id ? values?.subject_id : getId(subject),
                                student_ids: !!values?.is_for_all ? [] : values?.student_ids
                            },
                            _.isUndefined
                        )
                    )
                    return
                }

                const {ownerItem, groups, photo_preview, shared_url, ...rest} = values ?? {}
                onSubmit(
                    _.omitBy(
                        {
                            shared_url: normalizeShareUrl(shared_url),
                            ...rest,
                        },
                        _.isUndefined
                    )
                )
            }}>
            {(formik) => {
                const {values, touched, errors, setFieldValue, handleSubmit, isValid} = formik
                // touch NGAY khi đổi giá trị (không đợi blur/submit) - lỗi (vd time_close so với
                // time_open) hiện ngay trên màn hình vì Field ở đây gate qua touched.x && errors.x
                // (xem touchFields, ValidateForm.js).
                const onChange = (e) => {
                    setFieldValue(e.target.name, e.target.value)
                    touchFields(formik, e.target.name)
                }

                return (
                    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                        {/* Field trong form này đã tự hiện lỗi qua error={touched.x && errors.x} - nhưng
                            đó là lỗi TỪNG FIELD, không tự bảo vệ khỏi trường hợp field bị validate
                            (validateOnMount) trong khi Section chứa nó đang ẩn có điều kiện (isCourseClass/
                            isOrganization) - ErrorSummaries đọc thẳng errors qua FormikConsumer, không
                            phụ thuộc field nào đang render, nên vẫn hiện đúng khi rơi vào ca đó. */}
                        <ErrorSummaries/>
                        {isCourseClass && (
                            <CourseClassInformation
                                item={course_class}
                                showDescription={false}
                            />
                        )}

                        {/* Section "recipients" CHỈ 2 owner_type cần: course_class (subject/is_for_all/
                            students) và organization (chọn phòng ban con hay toàn tổ chức). group/course
                            KHÔNG hiện - phạm vi nhận đã cố định = đúng object đã chọn ở bước picker trước. */}
                        {(isCourseClass || isOrganization) && (
                            <Section icon={IoPeopleOutline} title={t('recipients')}>
                                {isCourseClass ? (
                                    <div className="flex flex-col gap-3">
                                        <Field label={t('subject')}>
                                            <SubjectInput name="subject" value={values.subject} form={formik}/>
                                        </Field>
                                        <Field label={t('for all students')}>
                                            <IsForAllInput name="is_for_all" value={values.is_for_all} onChange={onChange}/>
                                            <span className="text-2xs text-color-300 italic">{t('notify all students in the class')}</span>
                                        </Field>
                                        {/* Không dùng Pure/FormikConsumer để né remount như bản FieldsFactory cũ - JSX
                                            thuần render theo component type ổn định (StudentsInput hoist ở module
                                            scope) nên React không remount CourseClassStudentPicker mỗi keystroke ở
                                            field khác, chỉ FieldsFactory (schema là mảng tạo mới mỗi lần) mới bị vậy. */}
                                        {!values.is_for_all && (
                                            <Field label={t('students')}>
                                                <StudentsInput name="student_ids" value={values.student_ids} form={formik} onChange={onChange}/>
                                                <span className="text-2xs text-color-300 italic">{t('select specific students to receive this announcement')}</span>
                                            </Field>
                                        )}
                                    </div>
                                ) : (
                                    <GroupsInOrganizationField name="group_id" value={values.group_id} form={formik} onChange={onChange}/>
                                )}
                            </Section>
                        )}

                        <Section icon={IoMegaphoneOutline} title={t('notification content')} theme="highlight">
                            <Field label={t('title')} required error={touched.title && errors.title}>
                                <TitleInput name="title" value={values.title} onChange={onChange}/>
                            </Field>
                            <Field label={t('announcement type')} required error={touched.type && errors.type}>
                                <CategoryInput name="type" value={values.type} form={formik}/>
                            </Field>
                        </Section>

                        <Section icon={IoChatbubbleOutline} title={t('content')} subtitle={t('optional')}>
                            <Field label={t('content')}>
                                <ContentInput name="content" value={values.content} onChange={onChange}/>
                            </Field>
                            <Field label={t('image')}>
                                <PhotoInput name="photo" value={values.photo} form={formik} onChange={onChange}/>
                            </Field>
                        </Section>

                        <Section icon={IoCalendarOutline} title={t('display period')}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <Field label={t('started at')} required error={touched.time_open && errors.time_open}>
                                    <TimeOpenInput name="time_open" value={values.time_open} onChange={onChange}/>
                                </Field>
                                <Field label={t('finished at')} error={touched.time_close && errors.time_close}>
                                    <TimeCloseInput name="time_close" value={values.time_close} onChange={onChange}/>
                                    <span className="text-2xs text-color-300 italic">{t('leave finished at empty to expire at the end of the start date')}</span>
                                </Field>
                            </div>
                        </Section>

                        <Section icon={IoLinkOutline} title={t('additional info')} subtitle={t('optional')}>
                            <SharedUrlInput
                                name="shared_url"
                                value={values.shared_url}
                                onChange={onChange}
                                meta={{touched: touched.shared_url, error: errors.shared_url}}
                            />
                        </Section>

                        <FormActions onCancel={onCancel} submitText="create" isSubmitting={isSubmitting} isValid={isValid}/>
                    </form>
                )
            }}
        </Formik>
    )
}

export const AnnouncementUpdateForm = ({
                                           owner_type,
                                           ownerItem,
                                           groups,
                                           initialValues,
                                           onCancel = NullAsDefault,
                                           onSubmit = NullAsDefault,
                                           isSubmitting = false,
                                       }) => {
    const t = useTranslate()
    const isCourseClass = owner_type === 'course_class'
    const isOrganization = owner_type === 'organization'
    const course_class = isCourseClass ? ownerItem : undefined
    const ownerItemId = getId(ownerItem)

    const _initialValues = useMemo(() => {
        const {...rest} = initialValues ?? {}
        if (isCourseClass) {
            return {
                course_class_id: ownerItemId,
                course_class,
                owner_type,
                ...rest,
                subject: initialValues?.subject?.id,
                student_ids: initialValues?.announcement_course_class_students?.map(e => e?.student?.id) || [],
                subject_id: initialValues?.subject_id || initialValues?.subject?.id,
                photo_preview: initialValues?.photo,
            }
        }
        return {
            // rest?.[owner_type]: object owner THẬT của announcement đang sửa - AnnouncementTransformerBasic
            // trả field tên ĐÚNG owner_type (organization/group/course) nên lấy generic được, ưu tiên hơn
            // `ownerItem` (context hiện tại của hook) phòng khi lệch nhau (vd sửa 1 announcement 'group'
            // từ màn hình đang đứng ở scope 'organization' - xem comment GroupsInOrganizationField).
            ownerItem: rest?.[owner_type] ?? ownerItem,
            ...(isOrganization ? {groups, group_id: rest?.group?.id} : {}),
            owner_type,
            photo_preview: rest.photo,
            ...rest,
        }
    }, [owner_type, ownerItem, ownerItemId, groups, course_class, initialValues])

    return (
        <Formik
            validate={validate(t, 'Edit')}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={_initialValues}
            onSubmit={(values) => {
                const changes = shallowDiff(values, _initialValues)
                if (isCourseClass) {
                    const {shared_url, subject, time_open, time_close, ...rest} = changes ?? {}
                    onSubmit(
                        _.omitBy(
                            {
                                shared_url: normalizeShareUrl(shared_url),
                                time_open: getDateString(time_open),
                                time_close: getDateString(time_close),
                                ...rest,
                                subject_id: !!values?.subject_id ? values?.subject_id : getId(subject),
                                student_ids: !!values?.is_for_all ? [] : values?.student_ids
                            },
                            _.isUndefined
                        )
                    )
                    return
                }

                const {shared_url, ownerItem, ...rest} = changes ?? {}
                onSubmit(
                    _.omitBy(
                        {
                            shared_url: normalizeShareUrl(shared_url),
                            ...rest,
                        },
                        _.isUndefined
                    )
                )
            }}>
            {(formik) => {
                const {values, touched, errors, setFieldValue, handleSubmit, isValid} = formik
                // touch NGAY khi đổi giá trị (không đợi blur/submit) - lỗi (vd time_close so với
                // time_open) hiện ngay trên màn hình vì Field ở đây gate qua touched.x && errors.x
                // (xem touchFields, ValidateForm.js).
                const onChange = (e) => {
                    setFieldValue(e.target.name, e.target.value)
                    touchFields(formik, e.target.name)
                }

                return (
                    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                        <ErrorSummaries/>
                        {isCourseClass && (
                            <CourseClassInformation
                                item={course_class}
                                showDescription={false}
                            />
                        )}

                        {(isCourseClass || isOrganization) && (
                            <Section icon={IoPeopleOutline} title={t('recipients')}>
                                {isCourseClass ? (
                                    <div className="flex flex-col gap-3">
                                        <Field label={t('subject')}>
                                            <SubjectInput name="subject" value={values.subject} form={formik}/>
                                        </Field>
                                        <Field label={t('for all students')}>
                                            <IsForAllInput name="is_for_all" value={values.is_for_all} onChange={onChange}/>
                                            <span className="text-2xs text-color-300 italic">{t('notify all students in the class')}</span>
                                        </Field>
                                        {!values.is_for_all && (
                                            <Field label={t('students')}>
                                                <StudentsInput name="student_ids" value={values.student_ids} form={formik} onChange={onChange}/>
                                                <span className="text-2xs text-color-300 italic">{t('select specific students to receive this announcement')}</span>
                                            </Field>
                                        )}
                                    </div>
                                ) : (
                                    <GroupsInOrganizationField name="group_id" value={values.group_id} form={formik} onChange={onChange}/>
                                )}
                            </Section>
                        )}

                        <Section icon={IoMegaphoneOutline} title={t('notification content')} theme="highlight">
                            <Field label={t('title')} required error={touched.title && errors.title}>
                                <TitleInput name="title" value={values.title} onChange={onChange}/>
                            </Field>
                            <Field label={t('announcement type')} required error={touched.type && errors.type}>
                                <CategoryInput name="type" value={values.type} form={formik}/>
                            </Field>
                        </Section>

                        <Section icon={IoChatbubbleOutline} title={t('content')} subtitle={t('optional')}>
                            <Field label={t('content')}>
                                <ContentInput name="content" value={values.content} onChange={onChange}/>
                            </Field>
                            <Field label={t('image')}>
                                <PhotoInput name="photo" value={values.photo} form={formik} onChange={onChange}/>
                            </Field>
                        </Section>

                        <Section icon={IoCalendarOutline} title={t('display period')}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <Field label={t('started at')} required error={touched.time_open && errors.time_open}>
                                    <TimeOpenInput name="time_open" value={values.time_open} onChange={onChange}/>
                                </Field>
                                <Field label={t('finished at')} error={touched.time_close && errors.time_close}>
                                    <TimeCloseInput name="time_close" value={values.time_close} onChange={onChange}/>
                                    <span className="text-2xs text-color-300 italic">{t('leave finished at empty to expire at the end of the start date')}</span>
                                </Field>
                            </div>
                        </Section>

                        <Section icon={IoLinkOutline} title={t('additional info')} subtitle={t('optional')}>
                            <SharedUrlInput
                                name="shared_url"
                                value={values.shared_url}
                                onChange={onChange}
                                meta={{touched: touched.shared_url, error: errors.shared_url}}
                            />
                        </Section>

                        <FormActions onCancel={onCancel} submitText="save" isSubmitting={isSubmitting} isValid={isValid}/>
                    </form>
                )
            }}
        </Formik>
    )
}
